/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Monster from 'model/monster/Monster';
import Armor from 'model/stats/Armor';
import Attributes from 'model/stats/Attributes';
import AttributeValues from 'model/stats/AttributeValues';

export interface SavingThrows {
  handleUpdateArmor: (ac: number, desc: string, armor: Armor | null) => void;
  onToggleSavingThrow: (attribute: keyof AttributeValues) => void;
  savingThrowLevels: Map<keyof AttributeValues, number>;
}

const useSavingThrows = (
  mon: Monster,
  handleUpdateMonster: (field: string, value: any) => void,
): SavingThrows => {
  const handleUpdateArmor = (ac: number, desc: string, armor: Armor | null) => {
    handleUpdateMonster('armor_class', ac);
    handleUpdateMonster('armor_desc', desc);
    handleUpdateMonster('armor', armor);
  }

  const onToggleSavingThrow = (attribute: keyof AttributeValues) => {
    const attrShort = attribute.toUpperCase().substring(0, 3);
    const newSaves: string[] = [];
    let found = false;
    for (const save of mon.saving_throws?.split(/, */) || []) {
      if (save.toUpperCase().startsWith(attrShort)) {
        // If this is present in the saving_throws string, conintuing will remove it, because it won't be pushed into newSaves.
        found = true;
        continue;
      }
      newSaves.push(save);
    }
    if (!found) {
      // If it wasn't found, add it:
      newSaves.push(attribute);
    }
    newSaves.sort((a: string, b: string): number => {
      const findIndex = (attr: string) => {
        for (let i=0 ; i<Attributes.attributes.length ; i++) {
          if (Attributes.attributes[i].toUpperCase().startsWith(attr.toUpperCase().substring(0, 3))) {
            return i;
          }
        }
        return -1;
      }
      const indexA = findIndex(a);
      const indexB = findIndex(b);
      if (indexA == indexB) {
        return 0;
      }
      if (indexA > indexB) {
        return 1;
      }
      return -1;
    });
    handleUpdateMonster('saving_throws', newSaves.filter(o=>o).join(', '));
  }

  const savingThrowLevels = new Map<keyof AttributeValues, number>();
  for (const attr of Attributes.attributes) {
    const attrLevel = mon.attributes[attr];
    let level = Attributes.getBonus(attrLevel);
    savingThrowLevels.set(attr, level);
  }
  for (const save of mon.saving_throws?.toUpperCase().split(/, */) || []) {
    for (const attr of Attributes.attributes) {
      const attrShort = attr.toUpperCase().substring(0, 3);
      if (save.startsWith(attrShort)) {
        let newLevel = savingThrowLevels.get(attr)! + mon.proficiencyBonus;
        savingThrowLevels.set(attr, newLevel);
      }
    }
  }

  return { handleUpdateArmor, onToggleSavingThrow, savingThrowLevels };
};

export default useSavingThrows;
